import { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { t } from "@aawaz/i18n";
import { useQuery } from "@tanstack/react-query";
import { data, portal } from "@aawaz/api-client";
import { Button, Icon, Loading, Logo, NavContent, Shell, humanize, initials, type IconName, type NavItem } from "@aawaz/ui";
import { SessionProvider, useSession } from "./session";
import { useLiveUpdates } from "./live";
import { CommandPalette } from "./components/CommandPalette";
import { LoginPage, TenantPicker, TotpPage } from "./pages/Login";
import { HomePage } from "./pages/Home";
import { ClientDetailPage, ClientsPage } from "./pages/Clients";
import { DocumentsPage } from "./pages/Documents";
import { InboxPage } from "./pages/Inbox";
import { ChannelsPage } from "./pages/Channels";
import { BooksPage } from "./pages/Books";
import { ActivityPage } from "./pages/Activity";
import { SettingsPage } from "./pages/Settings";
import { GstDeskPage } from "./pages/GstDesk";

export function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/*" element={<Protected />} />
      </Routes>
    </SessionProvider>
  );
}

/** What the firm bought decides what it sees: "ca" adds the practice screens, "accounting" the books-first
 *  home; both together show everything. Everyone gets the inbox, documents, channels and settings. */
function useNav(needsAttention: number | undefined, joinRequests: number | undefined): NavItem[] {
  const s = useSession();
  const ca = s.has("ca");
  return [
    { to: "/", label: "Home", icon: "home", section: "Work" },
    { to: "/inbox", label: "Inbox", icon: "inbox", section: "Work", badge: needsAttention || undefined },
    { to: "/documents", label: "Documents", icon: "file", section: "Work" },
    ...(ca ? [
      { to: "/clients", label: "Clients", icon: "building" as IconName, section: "Practice" },
      { to: "/gst", label: "GST desk", icon: "receipt" as IconName, section: "Practice" },
    ] : []),
    { to: "/books", label: "Books", icon: "book", section: "Books" },
    { to: "/channels", label: "Channels", icon: "plug", section: "Setup", badge: joinRequests || undefined },
    { to: "/settings", label: t("nav.settings"), icon: "settings", section: "Setup" },
  ];
}

const TITLES: [RegExp, string][] = [
  [/^\/$/, "Home"], [/^\/inbox/, "Inbox"], [/^\/documents/, "Documents"], [/^\/clients\/./, "Client"], [/^\/clients/, "Clients"],
  [/^\/gst/, "GST desk"], [/^\/books/, "Books"], [/^\/(channels|connections)/, "Channels"], [/^\/settings/, "Settings"],
  [/^\/activity/, "Activity"],
];

export function editionLabel(eds: string[] | undefined): string {
  const e = new Set(eds ?? ["ca"]);
  return e.has("ca") && e.has("accounting") ? "CA practice + Accounting" : e.has("accounting") ? "Accounting" : "CA practice";
}

function Protected() {
  const s = useSession();
  const loc = useLocation();
  const [palette, setPalette] = useState(false);
  useLiveUpdates(!!s.me);
  // Inbox count for the menu; the home page shares this query, so it costs no extra request there.
  const dash = useQuery({ enabled: !!s.me, queryKey: ["dashboard"],
    queryFn: () => data<{ needs_attention: number; join_requests?: number }>(portal.GET("/portal/v1/dashboard")) });
  const nav = useNav(dash.data?.needs_attention, dash.data?.join_requests);
  const pages = useMemo(() => nav.map((n) => ({ label: n.label, to: n.to, icon: n.icon ?? "arrowRight" })), [nav]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPalette((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  if (s.loading) return <div className="ui-login"><Loading /></div>;
  if (s.needsTotp) return <TotpPage />;
  if (s.needsTenant) return <TenantPicker />;
  if (!s.me) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  const firmName = s.me.firm?.name ?? s.me.memberships.find((m) => m.tenant_id === s.me!.tenant_id)?.name ?? "Your firm";
  const title = TITLES.find(([re]) => re.test(loc.pathname))?.[1] ?? "";
  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  return (
    <Shell
      brand={<Logo product="Connect" onDark />}
      firm={(
        <a className="ui-firm" href={s.me.memberships.length > 1 ? "/switch" : "/settings"} title={s.me.memberships.length > 1 ? "Switch firm" : "Firm settings"}>
          <span className="ui-firm-mark" aria-hidden="true">{initials(firmName)}</span>
          <span style={{ minWidth: 0 }}>
            <span className="ui-firm-name">{firmName}</span>
            <span className="ui-firm-kind">{editionLabel(s.me.firm?.editions)}</span>
          </span>
        </a>
      )}
      nav={nav}
      renderLink={(n, close) => <NavLink to={n.to} end={n.to === "/"} onClick={close}><NavContent item={n} /></NavLink>}
      top={(
        <>
          <div className="ui-top-title"><span>{firmName}</span><Icon name="chevron" size={14} /><b>{title}</b></div>
          <div className="ui-top-actions">
            <button className="ui-search-trigger" onClick={() => setPalette(true)} aria-label="Search">
              <Icon name="search" size={16} /><span>Search pages and clients</span><kbd>{isMac ? "⌘" : "Ctrl"} K</kbd>
            </button>
          </div>
        </>
      )}
      footer={
        <div className="ui-user">
          <span className="ui-avatar" aria-hidden="true">{initials(s.me.name ?? firmName)}</span>
          <div className="ui-user-meta">
            <strong>{s.me.name ?? s.me.phone}</strong>
            <span>{humanize(s.me.role)}</span>
            {s.me.memberships.length > 1 ? <a href="/switch">Switch firm</a> : null}
          </div>
          <Button variant="ghost" className="ui-icon-btn" onClick={() => void s.logout()} aria-label={t("nav.logout")} title={t("nav.logout")}>
            <Icon name="logout" size={18} />
          </Button>
        </div>
      }
    >
      <CommandPalette open={palette} onClose={() => setPalette(false)} pages={pages} />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/clients" element={s.has("ca") ? <ClientsPage /> : <Navigate to="/" replace />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        <Route path="/gst" element={s.has("ca") ? <GstDeskPage /> : <Navigate to="/" replace />} />
        <Route path="/books" element={<BooksPage />} />
        <Route path="/channels/*" element={<ChannelsPage />} />
        {/* OAuth consent lands on /connections/<id>?connected=1; it opens the accounts tab with the link step */}
        <Route path="/connections/*" element={<ChannelsPage tab="accounts" />} />
        <Route path="/settings/*" element={<SettingsPage />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/switch" element={<TenantPicker />} />
        {/* earlier addresses */}
        <Route path="/review" element={<Navigate to="/inbox" replace />} />
        <Route path="/quarantine" element={<Navigate to="/inbox?tab=held" replace />} />
        <Route path="/whatsapp" element={<Navigate to="/channels/whatsapp" replace />} />
        <Route path="/mirror" element={<Navigate to="/channels/filing" replace />} />
        <Route path="/trust" element={<Navigate to="/settings/security" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
